import { GeneratePostsFrom } from "@/pages/Posts/Generate/GeneratePanel";
import { LoadingOutlined } from "@ant-design/icons";
import useThemes from "hooks/useThemes";
import { forwardRef, useMemo } from "react";
import { useAppSelector } from "store";
import { currentBusinessGetter } from "store/user/userSlice";
import { PostEditingDetails } from "../postEditorConstants";
import { InputDetail } from "./InputDetailComponents";

interface PostInputsPopoverContentProps {
  postDetail: PostEditingDetails;
  onOpenGeneratePanel: (generateFrom: GeneratePostsFrom) => void;
  handleToggleTheme: () => Promise<void>;
}

const PostInputsPopoverContent = forwardRef<
  HTMLDivElement,
  PostInputsPopoverContentProps
>(({ postDetail, onOpenGeneratePanel, handleToggleTheme }, ref) => {
  const currentBusiness = useAppSelector(currentBusinessGetter);

  const inputArchived = useMemo(
    () => ({
      topic: !currentBusiness.topics.find(
        ({ title, body }) =>
          postDetail.inputs?.topic.includes(title) ||
          postDetail.inputs?.topic.includes(body)
      ),
    }),
    [postDetail.inputs?.topic, currentBusiness.topics, currentBusiness.memories]
  );

  // If the list of themes gets too large, we could get the theme by id instead
  // which would be faster
  const { themes, isLoading } = useThemes();

  const theme = themes.find(
    (theme) => theme.name === postDetail?.template?.theme
  );

  return (
    <div ref={ref} className="w-64 flex flex-col gap-3">
      <InputDetail
        title="Topic"
        pagePath="/topics"
        onGenerate={() =>
          onOpenGeneratePanel({
            type: "topic",
            id: postDetail.inputs?.topic ?? "",
          })
        }
        disabled={!postDetail.inputs?.topic}
        inputArchived={inputArchived.topic}
        pluralizeTooltips
        currentBusiness={currentBusiness}
        handleToggleTheme={handleToggleTheme}
      >
        {postDetail.inputs?.topic ?? "N/A"}
      </InputDetail>
      <InputDetail
        title="Theme"
        pagePath="/themes"
        pluralizeTooltips
        currentBusiness={currentBusiness}
        handleToggleTheme={handleToggleTheme}
        themeName={theme?.name}
      >
        {isLoading ? (
          <LoadingOutlined className="text-antd-colorIcon ml-2" />
        ) : (
          theme?.display_name ?? "Unknown"
        )}
      </InputDetail>
    </div>
  );
});

export default PostInputsPopoverContent;
